import { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Plural, Trans } from '@lingui/react/macro';

import InlineText from '@/design_system/Typography/InlineText';
import IconCalendarOverdue from '@/icons/CalendarOverdue.svg';
import IconCalendarReminder from '@/icons/CalendarReminder.svg';
import { BaseNotification } from '@/layouts/App/Navbar/NotificationList/components/BaseNotification/BaseNotification';
import { useGetArticleName } from '@/models/article';
import type { TNotificationOfType } from '@/models/notification';

// Previously, this notification was associated to either a request or a shipment
// Now, it's always associated to a request and an article
export const DueDateAlertingNotification = ({
  notification,
}: {
  notification: TNotificationOfType<'due_date_alerting'>;
}) => {
  const { _ } = useLingui();
  const getArticleName = useGetArticleName();

  const taskWording = _(TASK_WORDING[notification.data.task]);
  const reference = notification.request?.reference ?? notification.shipment?.reference;
  const articleName = notification.article
    ? getArticleName({ article: notification.article })
    : undefined;

  const label =
    notification.data.dueDelay === 0
      ? articleName
        ? _(
            msg({
              id: 'notification.due-date.due-today.label.article',
              message: `You have ${taskWording} due today on the item ${articleName} from ${reference}`,
            })
          )
        : _(
            msg({
              id: 'notification.due-date.due-today.label',
              message: `You have ${taskWording} due today on ${reference}`,
            })
          )
      : articleName
        ? _(
            msg({
              id: 'notification.due-date.overdue.label.article',
              message: `You have ${taskWording} overdue on the item ${articleName} from ${reference}`,
            })
          )
        : _(
            msg({
              id: 'notification.due-date.overdue.label',
              message: `You have ${taskWording} overdue on ${reference}`,
            })
          );

  return (
    <BaseNotification
      icon={notification.data.dueDelay === 0 ? <IconCalendarReminder /> : <IconCalendarOverdue />}
      type={notification.data.dueDelay === 0 ? 'warning' : 'danger'}
      notification={notification}
      notificationLabel={label}
      notificationHeader={(color) =>
        notification.data.dueDelay === 0 ? (
          articleName ? (
            <Trans id="notification.due-date.due-today.article">
              You have {taskWording}{' '}
              <InlineText weight="medium" color={color}>
                due today
              </InlineText>{' '}
              on the item {articleName}.
            </Trans>
          ) : (
            <Trans id="notification.due-date.due-today">
              You have {taskWording}{' '}
              <InlineText weight="medium" color={color}>
                due today
              </InlineText>
              .
            </Trans>
          )
        ) : articleName ? (
          <Trans id="notification.due-date.overdue.article">
            You have {taskWording}{' '}
            <InlineText weight="medium" color={color}>
              overdue by <Plural value={notification.data.dueDelay} one="# day" other="# days" />
            </InlineText>{' '}
            on the item {articleName}.
          </Trans>
        ) : (
          <Trans id="notification.due-date.overdue">
            You have {taskWording}{' '}
            <InlineText weight="medium" color={color}>
              overdue by <Plural value={notification.data.dueDelay} one="# day" other="# days" />.
            </InlineText>
          </Trans>
        )
      }
    />
  );
};

const TASK_WORDING = {
  accept_dispatch: msg({
    id: 'notification.due-date.accept_dispatch',
    message: 'a job validation task',
  }),
  accept_dispatch_proposal: msg({
    id: 'notification.due-date.accept_dispatch_proposal',
    message: 'a quote review task',
  }),
  accept_requalification: msg({
    id: 'notification.due-date.accept_requalification',
    message: 'a new quote analysis task',
  }),
  add_customs_informations: msg({
    id: 'notification.due-date.add_customs_informations',
    message: 'a customs information task',
  }),
  adjust_service_during_processing: msg({
    id: 'notification.due-date.adjust_service_during_processing',
    message: 'a service review task',
  }),
  analyze_article: msg({
    id: 'notification.due-date.analyze_article',
    message: 'an analysis task',
  }),
  choose_article_service: msg({
    id: 'notification.due-date.choose_article_service',
    message: 'a diagnosis task',
  }),
  confirm_exchange_stock_availability: msg({
    id: 'notification.due-date.confirm_exchange_stock_availability',
    message: 'a stock availability confirmation task',
  }),
  contact_client: msg({
    id: 'notification.due-date.contact_client',
    message: 'a client outreach task',
  }),
  control_quality: msg({
    id: 'notification.due-date.control_quality',
    message: 'a quality control task',
  }),
  create_shipment: msg({
    id: 'notification.due-date.create_shipment',
    message: 'a shipment creation task',
  }),
  dropoff_shipment: msg({
    id: 'notification.due-date.dropoff_shipment',
    message: 'a shipment drop-off task',
  }),
  exchange_article: msg({
    id: 'notification.due-date.exchange_article',
    message: 'an exchange task',
  }),
  finalise_shipment: msg({
    id: 'notification.due-date.finalise_shipment',
    message: 'a shipment finalisation task',
  }),
  prepare_shipment: msg({
    id: 'notification.due-date.prepare_shipment',
    message: 'a shipment preparation task',
  }),
  repair_article: msg({
    id: 'notification.due-date.repair_article',
    message: 'a repair task',
  }),
  start_article_exchange: msg({
    id: 'notification.due-date.exchange_article',
    message: 'an exchange task',
  }),
  start_article_repair: msg({
    id: 'notification.due-date.repair_article',
    message: 'a repair task',
  }),
  validate_payment: msg({
    id: 'notification.due-date.validate_payment',
    message: 'a payment task',
  }),
  validate_refund: msg({
    id: 'notification.due-date.validate_refund',
    message: 'a refund task',
  }),
  validate_transit: msg({
    id: 'notification.due-date.validate_transit',
    message: 'a transit validation task',
  }),
  verify_shipment_reception: msg({
    id: 'notification.due-date.verify_shipment_reception',
    message: 'a shipment verification task',
  }),
} satisfies Record<TNotificationOfType<'due_date_alerting'>['data']['task'], MessageDescriptor>;
